import React, { useState } from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';

/**
 * JobCreationStep - Step 7: Create InvoiceDataCapture job records.
 */
const JobCreationStep = ({ wizard, api }) => {
  const [result, setResult] = useState(null);

  // Get pipeline mappings from step 5
  const getPipelineMappings = () => {
    const step5Data = wizard.getStepData(5);
    if (step5Data) {
      try {
        const parsed = JSON.parse(step5Data);
        return parsed.mappings || [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const handleCreate = async () => {
    try {
      const mappings = getPipelineMappings();
      if (mappings.length === 0) {
        return; // Error will show from API
      }

      // Get schema from step 6
      const step6Data = wizard.getStepData(6);
      let dataCaptureSchemaJson = null;
      if (step6Data) {
        try {
          const parsed = JSON.parse(step6Data);
          dataCaptureSchemaJson = parsed.generatedSchemaJson || null;
        } catch {
          // Fall through — backend will try to get from reference
        }
      }

      const jobData = {
        referenceCustomerId: wizard.referenceCustomerId,
        dataCaptureSchemaJson,
        mappings: mappings.map(m => ({
          pipelineJobId1: parseInt(m.pipelineJobId1),
          pipelineJobId2: parseInt(m.pipelineJobId2),
          workflowJobId: parseInt(m.workflowJobId),
          bfeJobId: m.bfeJobId ? parseInt(m.bfeJobId) : null,
          ipsnetJobId: m.ipsnetJobId ? parseInt(m.ipsnetJobId) : null,
          s3ImageBucketPath: m.s3ImageBucketPath || ''
        }))
      };

      const data = await api.createJobs(wizard.sessionId, jobData);
      setResult(data);
      wizard.completeStep(7);
    } catch {
      // Error shown by hook
    }
  };

  if (result) {
    return (
      <Alert variant="success">
        <Alert.Heading>Jobs Created Successfully</Alert.Heading>
        <p><strong>Job 1 ID:</strong> {result.job1Id} (Pipeline: {result.pipelineJobId1})</p>
        <p><strong>Job 2 ID:</strong> {result.job2Id} (Pipeline: {result.pipelineJobId2})</p>
        <p><strong>Verified:</strong> {result.verified ? '✓ Both records confirmed' : '✗ Verification pending'}</p>
        <p className="text-muted mb-0">Click "Next" to run validation checks.</p>
      </Alert>
    );
  }

  return (
    <div>
      <h5>Step 7: Create InvoiceDataCapture Jobs</h5>
      <p className="text-muted">
        This will create two job records in the InvoiceDataCapture database (main + VP) using the pipeline information from Step 5 and the schema from Step 6.
      </p>

      {api.error && <Alert variant="danger" dismissible onClose={api.clearError}>{api.error}</Alert>}

      {getPipelineMappings().length === 0 && (
        <Alert variant="warning">
          No pipeline mappings found. Please go back to Step 5 and save your pipeline information.
        </Alert>
      )}

      {getPipelineMappings().length > 0 && (
        <>
          <Alert variant="info">
            <strong>What will be created ({getPipelineMappings().length} mapping{getPipelineMappings().length > 1 ? 's' : ''}):</strong>
            <ul className="mb-0 mt-1">
              {getPipelineMappings().map((m, i) => (
                <li key={i}>Mapping {i + 1}: Main ID {m.pipelineJobId1}, VP ID {m.pipelineJobId2}, Workflow {m.workflowJobId}</li>
              ))}
              <li>A CustomerExternalMapping record for each mapping</li>
            </ul>
          </Alert>

          <Button variant="primary" onClick={handleCreate} disabled={api.loading}>
            {api.loading ? <><Spinner size="sm" className="me-2" />Creating Jobs...</> : 'Create Jobs'}
          </Button>
        </>
      )}
    </div>
  );
};

export default JobCreationStep;
